import { authService } from './authService'
import { getApiBaseUrl } from '../lib/apiBaseUrl'

// 📞 Utility function to convert E.164 to Thai format for user-auth service compatibility
const convertToThaiFormat = (phone: string): string => {
    // Remove all spaces, dashes, etc.
    const cleaned = phone.replace(/[^\d+]/g, '');

    // Handle different input formats
    if (cleaned.startsWith('+66')) {
        // E.164: +66616254799 → 0616254799
        return '0' + cleaned.substring(3);
    } else if (cleaned.startsWith('66') && cleaned.length === 11) {
        // International without +: 66616254799 → 0616254799
        return '0' + cleaned.substring(2);
    } else if (cleaned.startsWith('0') && cleaned.length === 10) {
        // Already Thai format: 0616254799
        return cleaned;
    } else if (cleaned.length === 9 && /^[689]/.test(cleaned)) {
        // Missing 0 prefix: 616254799 → 0616254799
        return '0' + cleaned;
    } else {
        // Unknown format - return as is and let validation handle it
        return cleaned;
    }
};

// ฟังก์ชันสำหรับ API request
const apiRequest = async (endpoint: string, options: any = {}) => {
    const tenantId = authService.getTenantId()
    const baseUrl = getApiBaseUrl()

    console.log('🔗 Registration API Request:', endpoint, {
        url: `${baseUrl}${endpoint}`,
        method: options.method || 'POST',
        tenantId: tenantId
    })

    const config: RequestInit = {
        method: options.method || 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Tenant-ID': tenantId,
            ...options.headers
        },
        ...options
    }
    // fetch() ต้องได้ body เป็น string สำหรับ JSON — ถ้าส่ง object ไปจะกลายเป็น "[object Object]"
    if (options.body !== undefined) {
        config.body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body)
    }

    try {
        const response = await fetch(`${baseUrl}${endpoint}`, config)

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}))
            throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`)
        }

        return await response.json()
    } catch (error: any) {
        console.error('❌ Registration API Request failed:', error)
        throw error
    }
}

/** เช็ค cooldown การขอ OTP สมัครจาก backend — ดักที่ backend ไม่ให้ขอซ้ำ */
export async function checkRegistrationOTPCooldown(mobile: string): Promise<{ allowed: boolean; remainingTimeMs?: number; remainingTimeMin?: number }> {
    const tenantId = authService.getTenantId()
    const baseUrl = getApiBaseUrl()
    const normalized = convertToThaiFormat(mobile)
    if (!/^0[689]\d{8}$/.test(normalized)) return { allowed: true }
    try {
        const res = await fetch(
            `${baseUrl}/api/v1/user-auth/check-registration-otp-cooldown?tenantId=${encodeURIComponent(tenantId)}&mobile=${encodeURIComponent(normalized)}`,
            { method: 'GET', headers: { 'X-Tenant-ID': tenantId }, credentials: 'include' }
        )
        const data = await res.json().catch(() => ({}))
        if (!res.ok) return { allowed: true }
        return { allowed: data.allowed !== false, remainingTimeMs: data.remainingTimeMs, remainingTimeMin: data.remainingTimeMin }
    } catch {
        return { allowed: true }
    }
}

/** บันทึกว่าเพิ่งขอ OTP สมัครแล้ว (ใช้หลัง Firebase ส่ง OTP สำเร็จ) */
export async function recordRegistrationOTPSent(mobile: string): Promise<boolean> {
    const tenantId = authService.getTenantId()
    const baseUrl = getApiBaseUrl()
    const normalized = convertToThaiFormat(mobile)
    if (!/^0[689]\d{8}$/.test(normalized)) return false
    try {
        const res = await fetch(`${baseUrl}/api/v1/user-auth/record-registration-otp-sent`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Tenant-ID': tenantId },
            credentials: 'include',
            body: JSON.stringify({ tenantId, mobile: normalized }),
        })
        const data = await res.json().catch(() => ({}))
        return data.success === true
    } catch {
        return false
    }
}

// Types for registration
export interface PhoneVerificationRequest {
    phoneNumber: string
    step: 'verify_phone'
}

export interface SMSConfigResponse {
    success: boolean
    data?: {
        smsEnabled: boolean
        requiresRecaptcha: boolean
        primaryProvider: string
        providers: Array<{
            id: string
            type: string
            priority: number
        }>
        hasFirebaseFallback: boolean
        fallbackEnabled: boolean
    }
    error?: string
}

export interface PhoneVerificationResponse {
    success: boolean
    message?: string
    error?: string
    data?: {
        sessionId?: string
        verificationId?: string
        expiresAt?: number | string
        mobile?: string
        provider?: string
        cost?: number
        canResendAfter?: number
    }
}

export interface SmsVerificationRequest {
    phoneNumber: string
    verificationCode: string
    verificationId: string
    step: 'verify_sms'
}

export interface SmsVerificationResponse {
    success: boolean
    message?: string
    error?: string
    data?: {
        phoneVerified: boolean
        nextStep: string
    }
}

export interface UserRegistrationRequest {
    phoneNumber: string
    verificationId: string
    username: string
    password: string
    confirmPassword: string
    firstName?: string
    lastName?: string
    referralCode?: string
    step: 'complete_registration'
}

export interface UserRegistrationResponse {
    success: boolean
    message?: string
    error?: string
    data?: {
        userId: string
        username: string
        message: string
    }
}

const normalizeSmsProviders = (providers: unknown): Array<{ id: string; type: string; priority: number }> => {
    if (!Array.isArray(providers)) {
        return []
    }

    return providers
        .map((provider: any) => {
            const type = typeof provider?.type === 'string' ? provider.type.trim() : ''
            if (!type) {
                return null
            }

            return {
                id: typeof provider?.id === 'string' && provider.id.trim()
                    ? provider.id.trim()
                    : type,
                type,
                priority: Number.isFinite(provider?.priority) ? provider.priority : 999
            }
        })
        .filter(Boolean) as Array<{ id: string; type: string; priority: number }>
}

export const registerService = {
    // ดึงข้อมูล SMS configuration สำหรับ registration
    async getSMSConfig(): Promise<SMSConfigResponse> {
        try {
            const tenantId = authService.getTenantId()
            const baseUrl = getApiBaseUrl()
            const requestTs = Date.now()
            const basePath = `${baseUrl}/api/v1/configuration-service/tenant-sms/${tenantId}`
            const publicProvidersUrl = `${basePath}/providers/public?_t=${requestTs}`
            const fullConfigUrl = `${basePath}?_t=${requestTs}`

            // 1) เรียก Public endpoint สำหรับ providers (ไม่ต้องส่ง service token) — ใช้สำหรับหน้า Register
            try {
                const publicProvidersRes = await fetch(publicProvidersUrl, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Tenant-ID': tenantId,
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                        'Expires': '0'
                    },
                    cache: 'no-store'
                })
                if (publicProvidersRes.ok) {
                    const publicJson = await publicProvidersRes.json()
                    if (publicJson.success && Array.isArray(publicJson.providers)) {
                        const providers = normalizeSmsProviders(publicJson.providers)
                        const primaryProvider = publicJson.primaryProvider || providers[0]?.type || (publicJson.smsEnabled === true ? 'firebase' : 'none')
                        const hasFirebase = providers.some((p: any) => p.type === 'firebase')
                        const transformedData: SMSConfigResponse = {
                            success: true,
                            data: {
                                smsEnabled: publicJson.smsEnabled === true,
                                requiresRecaptcha: hasFirebase,
                                primaryProvider,
                                providers,
                                hasFirebaseFallback: hasFirebase,
                                fallbackEnabled: providers.length > 1
                            }
                        }
                        console.log('📱 SMS config loaded from public providers:', {
                            smsEnabled: transformedData.data?.smsEnabled,
                            primaryProvider: transformedData.data?.primaryProvider,
                            providersCount: transformedData.data?.providers?.length || 0
                        })
                        return transformedData
                    }
                }
            } catch (_) { /* ignore */ }

            // 2) Fallback: เรียก full config (ต้องมี service token)
            const response = await fetch(fullConfigUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-ID': tenantId,
                    'X-Service-Name': 'user-classic-frontend',
                    'X-Service-Token': (import.meta.env as any).VITE_SERVICE_TOKEN || 'huay-secret-key-2024',
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0'
                },
                cache: 'no-store'
            })

            if (!response.ok) {
                return {
                    success: true,
                    data: {
                        smsEnabled: false,
                        requiresRecaptcha: false,
                        primaryProvider: '',
                        providers: [],
                        hasFirebaseFallback: false,
                        fallbackEnabled: false
                    }
                }
            }

            const result = await response.json()

            // ✅ Transform data from Configuration Service to match frontend expectations
            if (result.success && result.data) {
                const config = result.data
                const enabledProviders = config.preferredProviders?.filter((p: any) => p.enabled) || []

                // Sort by priority (1 = highest priority)
                enabledProviders.sort((a: any, b: any) => a.priority - b.priority)

                const primaryProvider = enabledProviders[0]
                const firebaseProvider = enabledProviders.find((p: any) => p.provider === 'firebase')

                const transformedData: SMSConfigResponse = {
                    success: true,
                    data: {
                        smsEnabled: config.smsEnabled === true,
                        requiresRecaptcha: !!firebaseProvider,
                        primaryProvider: primaryProvider?.provider || 'none',
                        providers: enabledProviders.map((p: any) => ({
                            id: p.provider,
                            type: p.provider,
                            priority: p.priority
                        })),
                        hasFirebaseFallback: !!firebaseProvider,
                        fallbackEnabled: enabledProviders.length > 1
                    }
                }

                console.log('📱 SMS config loaded from Configuration Service:', {
                    success: true,
                    smsEnabled: transformedData.data?.smsEnabled,
                    requiresRecaptcha: transformedData.data?.requiresRecaptcha,
                    primaryProvider: transformedData.data?.primaryProvider,
                    providersCount: transformedData.data?.providers?.length || 0
                })

                return transformedData
            }

            return {
                success: true,
                data: {
                    smsEnabled: false,
                    requiresRecaptcha: false,
                    primaryProvider: '',
                    providers: [],
                    hasFirebaseFallback: false,
                    fallbackEnabled: false
                }
            }

        } catch (error: any) {
            console.error('❌ Get SMS config failed:', error)

            // Return disabled SMS config instead of authenticated fallback
            return {
                success: true,
                data: {
                    smsEnabled: false,
                    requiresRecaptcha: false,
                    primaryProvider: '',
                    providers: [],
                    hasFirebaseFallback: false,
                    fallbackEnabled: false
                }
            }
        }
    },

    // 🔄 Fallback method for tenant-management
    async getSMSConfigFallback(tenantId: string, baseUrl: string): Promise<SMSConfigResponse> {
        try {
            const response = await fetch(`${baseUrl}/api/v1/tenant-management/sms/providers`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-ID': tenantId
                }
            })

            if (!response.ok) {
                throw new Error(`Tenant-management SMS config failed: ${response.status}`)
            }

            const result = await response.json()

            if (result.success && result.data && Array.isArray(result.data)) {
                const providers = result.data
                const enabledProviders = providers.filter((p: any) => p.enabled)
                const primaryProvider = enabledProviders.find((p: any) => p.priority === 1) || enabledProviders[0]

                const firebaseProvider = providers.find((p: any) => p.type === 'firebase')
                const requiresRecaptcha = firebaseProvider?.enabled && firebaseProvider?.config?.projectId

                const transformedData: SMSConfigResponse = {
                    success: true,
                    data: {
                        smsEnabled: enabledProviders.length > 0,
                        requiresRecaptcha: !!requiresRecaptcha,
                        primaryProvider: primaryProvider?.type || 'none',
                        providers: enabledProviders.map((p: any) => ({
                            id: p.providerId || p.id,
                            type: p.type,
                            priority: p.priority || 999
                        })),
                        hasFirebaseFallback: !!firebaseProvider?.enabled,
                        fallbackEnabled: enabledProviders.length > 1
                    }
                }

                console.log('📱 SMS config loaded from tenant-management fallback:', {
                    success: true,
                    smsEnabled: transformedData.data?.smsEnabled,
                    primaryProvider: transformedData.data?.primaryProvider,
                    providersCount: transformedData.data?.providers?.length || 0
                })

                return transformedData
            } else {
                throw new Error('Invalid response from tenant-management fallback')
            }

        } catch (error: any) {
            throw new Error(`Fallback failed: ${error.message}`)
        }
    },

    // ตรวจสอบว่าเบอร์มือถือมีในระบบแล้วหรือไม่
    async checkMobileAvailability(phoneNumber: string): Promise<{
        success: boolean
        available: boolean
        message?: string
        smsConfig?: {
            enabled: boolean
            verificationRequired: boolean
            canSkipVerification: boolean
        }
        nextStep?: 'sms_verification' | 'complete_registration' | 'redirect_to_login'
        existingUser?: {
            username: string
            status: string
            registeredAt: string
            lastLogin?: string
        }
        error?: string
    }> {
        try {
            const cleanedPhone = phoneNumber.replace(/[-\s]/g, '')
            const thaiFormatPhone = convertToThaiFormat(cleanedPhone);

            console.log('🔍 Check mobile format conversion:', {
                original: phoneNumber,
                cleaned: cleanedPhone,
                thaiFormat: thaiFormatPhone
            });

            const response = await apiRequest(`/api/v1/user-auth/check-mobile/${thaiFormatPhone}`, {
                method: 'GET'
            })

            console.log('📱 Mobile availability check response:', {
                success: response.success,
                available: response.available,
                hasExistingUser: !!response.existingUser
            })

            return response
        } catch (error: any) {
            console.error('❌ Check mobile availability failed:', error)
            return {
                success: false,
                available: false,
                error: error.message || 'ไม่สามารถตรวจสอบเบอร์มือถือได้'
            }
        }
    },

    // ขั้นตอนที่ 1: ส่ง SMS verification พร้อม Priority-based Fallback
    async sendPhoneVerification(phoneNumber: string, recaptchaToken?: string): Promise<PhoneVerificationResponse> {
        try {
            const cleanedPhone = phoneNumber.replace(/[-\s]/g, '')
            const tenantId = authService.getTenantId()

            const thaiFormatPhone = convertToThaiFormat(cleanedPhone);
            console.log('📞 Phone number format conversion:', {
                original: phoneNumber,
                cleaned: cleanedPhone,
                thaiFormat: thaiFormatPhone,
                isValidThai: /^0[689]\d{8}$/.test(thaiFormatPhone)
            });

            // 📱 Get SMS configuration for fallback logic
            const smsConfig = await this.getSMSConfig()
            if (!smsConfig.success || !smsConfig.data?.smsEnabled) {
                throw new Error('SMS service is not enabled for this tenant')
            }

            const providers = smsConfig.data.providers.sort((a, b) => a.priority - b.priority)
            console.log('🔄 Available SMS providers for fallback:', providers.map((p: any) => `${p.type}(${p.priority})`))

            // 📞 Generate OTP code
            const otpCode = Math.floor(100000 + Math.random() * 900000).toString()
            const message = `รหัส OTP สำหรับสมัครสมาชิก: ${otpCode} (ใช้ได้ 5 นาที)`

            // ✅ Try each provider in priority order with fallback
            let lastError = null

            for (let i = 0; i < providers.length; i++) {
                const provider = providers[i]
                console.log(`📡 Attempting SMS via ${provider.type} (priority ${provider.priority})`)

                try {
                    const requestBody: any = {
                        tenantId: tenantId,
                        phoneNumber: cleanedPhone,
                        message: message,
                        messageType: 'otp',
                        metadata: {
                            source: 'registration',
                            otpCode: otpCode,
                            isRegistration: true,
                            attemptNumber: i + 1,
                            fallbackEnabled: providers.length > 1,
                            provider: provider.type
                        }
                    }

                    // Add reCAPTCHA token if Firebase and provided
                    if (provider.type === 'firebase' && recaptchaToken) {
                        requestBody.metadata.recaptchaToken = recaptchaToken
                        console.log('🔐 Including reCAPTCHA token for Firebase authentication')
                    }

                    // 🎯 Strategy Change: Use tenant-admin pattern
                    // Firebase providers → Client SDK (handled in frontend)
                    // Non-Firebase → Backend service

                    if (provider.type === 'firebase') {
                        // Firebase should use Client SDK (like TestSMSModal)
                        // This should not happen if RegisterPage.tsx logic is correct
                        throw new Error('Firebase should use Client SDK directly in RegisterPage.tsx');
                    } else {
                        // Non-Firebase providers → Use user-auth service (proper service for registration)
                        const response = await apiRequest('/api/v1/user-auth/send-registration-otp', {
                            method: 'POST',
                            body: JSON.stringify({
                                tenantId: tenantId,
                                mobile: thaiFormatPhone // Use properly converted Thai format
                            })
                        })

                        if (response.success) {
                            // ✅ Success! user-auth service response
                            const sessionId = response.sessionId || `reg_${Date.now()}`
                            localStorage.setItem(`otp_${sessionId}`, otpCode)
                            localStorage.setItem(`otp_expires_${sessionId}`, (Date.now() + 5 * 60 * 1000).toString())

                            console.log(`✅ Registration OTP sent successfully via user-auth service:`, {
                                success: true,
                                sessionId: sessionId,
                                provider: provider.type,
                                phoneNumber: thaiFormatPhone.replace(/\d(?=\d{4})/g, '*'),
                                attemptNumber: i + 1,
                                fallbackUsed: i > 0
                            })

                            return {
                                success: true,
                                message: `SMS sent successfully via user-auth service`,
                                data: {
                                    sessionId: sessionId,
                                    verificationId: sessionId,
                                    expiresAt: Date.now() + 5 * 60 * 1000,
                                    mobile: thaiFormatPhone,
                                    provider: provider.type,
                                    canResendAfter: response.canResendAfter || 60 * 1000
                                }
                            }
                        } else {
                            throw new Error(response.message || response.error || `${provider.type} failed`)
                        }
                    }

                } catch (providerError: any) {
                    console.warn(`⚠️ ${provider.type} failed (attempt ${i + 1}):`, providerError.message)
                    lastError = providerError

                    // Continue to next provider if available
                    if (i < providers.length - 1) {
                        console.log(`🔄 Falling back to next provider...`)
                        continue
                    }
                }
            }

            // ❌ All providers failed
            console.error('❌ All SMS providers failed for registration')
            throw new Error(`SMS verification unavailable. All providers failed. Last error: ${lastError?.message || 'Unknown error'}`)

        } catch (error: any) {
            console.error('❌ Send phone verification failed:', error)

            // ✅ Better error messaging for users
            let errorMessage = 'ไม่สามารถส่ง SMS ได้'

            if (error.message?.includes('All providers failed')) {
                errorMessage = 'ระบบ SMS มีปัญหา กรุณาลองใหม่อีกครั้งในภายหลัง'
            } else if (error.message?.includes('not enabled')) {
                errorMessage = 'ระบบ SMS ไม่พร้อมใช้งานสำหรับ tenant นี้'
            } else if (error.message?.includes('RATE_LIMIT')) {
                errorMessage = 'ส่ง SMS บ่อยเกินไป กรุณารอสักครู่'
            }

            return {
                success: false,
                error: errorMessage
            }
        }
    },

    // ขั้นตอนที่ 2: ยืนยัน SMS code
    async verifySmsCode(
        phoneNumber: string,
        verificationCode: string,
        verificationId: string
    ): Promise<SmsVerificationResponse> {
        try {
            const cleanedPhone = phoneNumber.replace(/[-\s]/g, '')
            const thaiFormatPhone = convertToThaiFormat(cleanedPhone);

            const response = await apiRequest('/api/v1/user-auth/verify-registration-otp', {
                method: 'POST',
                body: JSON.stringify({
                    tenantId: authService.getTenantId(),
                    mobile: thaiFormatPhone,
                    otpCode: verificationCode,
                    sessionId: verificationId
                })
            })

            console.log('💬 SMS verification response:', {
                success: response.success,
                phoneVerified: response.data?.phoneVerified
            })

            return response
        } catch (error: any) {
            console.error('❌ SMS verification failed:', error)
            return {
                success: false,
                error: error.message || 'รหัสยืนยันไม่ถูกต้อง'
            }
        }
    },


    // ฟังก์ชันตรวจสอบเบอร์โทรไทย
    validateThaiPhoneNumber(phoneNumber: string): boolean {
        const cleaned = phoneNumber.replace(/[-\s]/g, '')

        // ตรวจสอบความยาว 10 หลัก
        if (!/^[0-9]{10}$/.test(cleaned)) {
            return false
        }

        // ตรวจสอบเบอร์ต้นทางมือถือไทย
        const validPrefixes = ['06', '08', '09']
        return validPrefixes.some(prefix => cleaned.startsWith(prefix))
    },

    // ฟังก์ชันตรวจสอบรหัสผ่าน
    validatePassword(password: string): { valid: boolean; message?: string } {
        if (password.length < 6) {
            return { valid: false, message: 'รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร' }
        }

        if (password.length > 50) {
            return { valid: false, message: 'รหัสผ่านต้องมีความยาวไม่เกิน 50 ตัวอักษร' }
        }

        return { valid: true }
    },

    // ฟังก์ชันตรวจสอบชื่อผู้ใช้ (client-side validation)
    validateUsername(username: string): { valid: boolean; message?: string } {
        if (username.length < 4) {
            return { valid: false, message: 'ชื่อผู้ใช้ต้องมีความยาวอย่างน้อย 4 ตัวอักษร' }
        }

        if (username.length > 20) {
            return { valid: false, message: 'ชื่อผู้ใช้ต้องมีความยาวไม่เกิน 20 ตัวอักษร' }
        }

        if (!/^[a-zA-Z0-9_]+$/.test(username)) {
            return { valid: false, message: 'ชื่อผู้ใช้ต้องประกอบด้วยภาษาอังกฤษ ตัวเลข และ _ เท่านั้น' }
        }

        return { valid: true }
    },

    // ฟังก์ชันตรวจสอบชื่อผู้ใช้จาก API
    async validateUsernameAPI(username: string): Promise<{ success: boolean; available: boolean; message: string; suggestions?: string[] }> {
        try {
            console.log('🔍 Validating username via API:', username.replace(/\w(?=\w{2})/g, '*'))

            const response = await apiRequest(`/api/v1/user-auth/check-username/${encodeURIComponent(username)}`, {
                method: 'GET',
                headers: {
                    'X-Tenant-ID': authService.getTenantId()
                }
            })

            console.log('✅ Username validation response:', response)
            return response
        } catch (error: any) {
            console.error('❌ Username validation error:', error)
            return {
                success: false,
                available: false,
                message: 'เกิดข้อผิดพลาดในการตรวจสอบชื่อผู้ใช้'
            }
        }
    },

    /**
     * Check if bank + account number already used in tenant (for complete page — disable submit if taken)
     */
    async checkBankAccountAvailability(bankCode: string, accountNumber: string): Promise<{ success: boolean; available: boolean; message?: string }> {
        try {
            const tenantId = authService.getTenantId()
            const baseUrl = getApiBaseUrl()
            const code = (bankCode || '').trim()
            const num = (accountNumber || '').replace(/\D/g, '')
            if (!code || num.length < 10 || num.length > 12) return { success: true, available: true }
            const params = new URLSearchParams({ tenantId, bankCode: code, accountNumber: num })
            const res = await fetch(`${baseUrl}/api/v1/user-auth/check-bank-account?${params}`, {
                method: 'GET',
                headers: { 'X-Tenant-ID': tenantId },
                credentials: 'include'
            })
            const data = await res.json()
            if (!res.ok) return { success: false, available: true }
            return {
                success: true,
                available: data.available === true,
                message: data.message
            }
        } catch {
            return { success: false, available: true }
        }
    },

    /**
     * Complete user registration
     * Handles both SMS-verified and non-SMS registration flows
     */
    async completeRegistration(
        phoneNumber: string,
        username: string,
        password: string,
        fullName: string,
        bankCode: string,
        accountNumber: string,
        sessionId: string,
        smsVerified: boolean,
        method: string,
        bankImageUrl?: string,
        referralCode?: string
    ): Promise<{ success: boolean; message: string; data?: any; error?: string }> {
        try {
            console.log('🎯 Completing registration:', {
                phoneNumber: phoneNumber.replace(/(\d{3})\d{4}(\d{2})/, '$1****$2'),
                username,
                fullName,
                bankCode,
                bankImageUrl,
                smsVerified,
                method,
                hasReferralCode: !!referralCode
            })

            const body: Record<string, unknown> = {
                tenantId: authService.getTenantId(),
                phoneNumber,
                username,
                password,
                fullName,
                bankCode,
                accountNumber,
                bankImageUrl,
                sessionId,
                smsVerified,
                method
            }
            if (referralCode && referralCode.trim()) {
                body.referralCode = referralCode.trim()
            }

            const response = await apiRequest('/api/v1/user-auth/complete-registration', {
                method: 'POST',
                body
            })

            console.log('✅ Registration completed successfully:', response)

            return {
                success: true,
                message: response.message || 'สมัครสมาชิกสำเร็จ',
                data: response.data
            }
        } catch (error: any) {
            console.error('❌ Complete registration error:', error)

            const errorMessage = error.response?.data?.message ||
                error.message ||
                'เกิดข้อผิดพลาดในการสมัครสมาชิก'

            return {
                success: false,
                message: errorMessage,
                error: error.response?.data?.error || 'REGISTRATION_FAILED'
            }
        }
    }
}
